import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { config } from '../config.js';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * QR 오버레이. 우측 하단.
 *
 * QR 은 **클라이언트에서 생성한다** — 서버 왕복이 없어 URL 이 정해진 즉시 뜬다
 * (`docs/06-frontend-spec.md` 「QR 코드」).
 *
 * 생성이 비동기(`toDataURL`)라 첫 프레임에는 이미지가 없다. 자리를 미리 잡아 두고 채운다 —
 * 크기가 나중에 변하면 재생 중인 화면에서 오버레이가 튄다.
 *
 * > **T8 에서 URL 을 밖에서 받도록 바꿨다.** 전에는 이 컴포넌트가 `purchaseQrUrl` 로
 * > BLOOM 랜딩 주소를 직접 조립했는데, 이제 담기는 것이 경로마다 다르다 —
 * > Tier 4 는 서버가 준 `qrUrl`(`/q/{sessionToken}`)이고, 핸드오프가 실패했을 때의
 * > 폴백은 여전히 BLOOM 랜딩(`/p/{productId}` + 추적 4파라미터)이다. **어느 쪽을 그릴지는
 * > 화면의 결정이지 QR 그리기의 결정이 아니다.**
 */
export function QrOverlay({
  url,
  // **기본값을 사전에서 채울 수 없다** — 기본 인자는 훅 밖에서 평가된다.
  // `undefined` 를 받아 본문에서 채운다.
  hint,
  size = 200,
  bare = false,
}: {
  url: string;
  hint?: string;
  /** 렌더 크기(px). 가구 경로의 곁들임 QR 은 작게 그린다 (`docs/21` W8) */
  size?: number;
  /**
   * 오버레이 껍데기 없이 QR 만. **다른 오버레이 안에 들어갈 때 쓴다** — `overlay` 를
   * 겹치면 위치 지정이 두 번 걸려 화면 밖으로 나간다.
   */
  bare?: boolean;
}) {
  const t = useT();
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void QRCode.toDataURL(url, { width: size, margin: 1 }).then(
      (png) => {
        if (alive) setDataUrl(png);
      },
      () => {
        // 생성 실패로 재생을 건드리지 않는다. 자리만 비워 둔다.
      },
    );
    return () => {
      alive = false;
    };
  }, [url, size]);

  const body = (
    <>
      <div className="overlay__qr-frame">
        {dataUrl !== null && (
          <img
            src={dataUrl}
            alt={t('qr.alt')}
            width={size}
            height={size}
            /*
             * `?debug=1` 에서만 붙는다. QR 은 이미지라 무엇을 담고 있는지 화면에서도
             * 자동 검증에서도 읽을 수 없는데, 담긴 것이 추적의 전부다 (CLAUDE.md 절대
             * 규칙 8). 인코더에 넘긴 문자열을 그대로 노출해 "스캔하면 어디로 가는가"를
             * 확인할 수 있게 한다. **하네스가 이 속성으로 QR 내용을 읽는다** —
             * 관례를 유지할 것 (`docs/09` T8).
             */
            data-qr-url={config.debug ? url : undefined}
          />
        )}
      </div>
      <p className="overlay__qr-hint">{hint ?? t('qr.scanHint')}</p>
    </>
  );

  return bare ? body : <div className="overlay overlay--qr">{body}</div>;
}
